import { useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import { Box } from "@mui/material";
import ImageCropp, {
  ImageCroppRef,
} from "src/Components/ImageCropp/ImageCropp";
import { ICON_OUTPUT_SIZE, ICON_VIEWPORT } from "./constants";

export interface IconCropperRef {
  submitCrop: () => Promise<void>;
}

interface IconCropperProps {
  imageSrc: string;
  onCancel: () => void;
  onCropped: (file: File) => void;
}

const IconCropper = forwardRef<IconCropperRef, IconCropperProps>(
  ({ imageSrc, onCancel, onCropped }, ref) => {
    const cropperRef = useRef<ImageCroppRef>(null);

    const handleCropSave = useCallback(
      (croppedImage: string | Blob) => {
        const file =
          croppedImage instanceof Blob
            ? new File([croppedImage], "icon.png", { type: "image/png" })
            : (() => {
                const byteString = atob(croppedImage.split(",")[1]);
                const mimeString = croppedImage
                  .split(",")[0]
                  .split(":")[1]
                  .split(";")[0];
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i += 1) {
                  ia[i] = byteString.charCodeAt(i);
                }
                return new File([ab], "icon.png", { type: mimeString });
              })();
        onCropped(file);
      },
      [onCropped]
    );

    useImperativeHandle(ref, () => ({
      submitCrop: async () => {
        if (cropperRef.current) {
          await cropperRef.current.submitCrop();
        }
      },
    }));

    return (
      <Box
        sx={{
          maxWidth: "736px",
          overflowX: "hidden",
          "&.MuiBox-root": { margin: "1rem 0 0 0" },
        }}
      >
        <ImageCropp
          ref={cropperRef}
          image={imageSrc}
          outputSize={ICON_OUTPUT_SIZE}
          cropSize={ICON_OUTPUT_SIZE}
          outputType="blob"
          croppieConfig={{ viewport: ICON_VIEWPORT }}
          onSave={handleCropSave}
          onDelete={onCancel}
        />
      </Box>
    );
  }
);

IconCropper.displayName = "IconCropper";

export default IconCropper;
